import { useDebounce, useGetJob } from "@api";
import type { TJSONSchema } from "@artifact-management/json-dynamic-form";
import WbIncandescentOutlinedIcon from "@mui/icons-material/WbIncandescentOutlined";
import { InputAdornment } from "@mui/material";
import { useCallback, useEffect } from "react";
import { type FieldValues, type Path, type PathValue, useController, useWatch } from "react-hook-form";
import { AsyncAutocomplete } from "./AsyncAutocomplete";
import type { IFormGenInputProps } from "./index";

export type TAppellationOption = { id: string; label: string; romeCode?: string; romeLabel?: string };

export const FormGenInputAppellation = <Schema extends TJSONSchema, T extends FieldValues>({
  control,
  setValue,
  uiSchemaElement,
  schema,
  multiple,
}: IFormGenInputProps<Schema, T>) => {
  const [{ loading }, getJobs] = useGetJob();
  const { field, fieldState } = useController({
    control,
    name: uiSchemaElement.scope as unknown as Path<T>,
    defaultValue: (multiple ? [] : null) as PathValue<T, Path<T>>,
  });
  const appellationSourceProperty = schema.jsonSchema.$defs?.appellationSourceProperty as
    | { const?: string }
    | undefined;
  const nameValue = useWatch({
    control,
    name: appellationSourceProperty?.const as unknown as Path<T>,
  }) as string | undefined;

  const fetchOptions = useCallback(
    async (query: string): Promise<TAppellationOption[]> => {
      const result = await getJobs({ libelle: query });
      if (result.status !== 200) {
        return [];
      }
      return result.data.data.map((job) => ({
        id: job._id,
        label: job.libelle,
        romeCode: job.rome?._id,
        romeLabel: job.rome?.libelle,
      }));
    },
    [getJobs]
  );

  const handleNameDebounce = useDebounce(
    (value: string) => {
      fetchOptions(value).then((options) => {
        setValue(
          uiSchemaElement.scope as unknown as Path<T>,
          (multiple ? options.slice(0, 1) : (options[0] ?? null)) as PathValue<T, Path<T>>
        );
      });
    },
    750,
    [fetchOptions, multiple, setValue, uiSchemaElement.scope]
  );

  useEffect(() => {
    if (!nameValue) {
      setValue(uiSchemaElement.scope as unknown as Path<T>, (multiple ? [] : null) as PathValue<T, Path<T>>);
      return;
    }
    const current = field.value as TAppellationOption | TAppellationOption[] | undefined;
    const alreadyMatches = Array.isArray(current)
      ? current.some((option) => option.label === nameValue)
      : current?.label === nameValue;
    if (alreadyMatches) {
      return;
    }
    handleNameDebounce(nameValue);
  }, [nameValue, handleNameDebounce, setValue, uiSchemaElement.scope, multiple]);

  return (
    <AsyncAutocomplete
      label={uiSchemaElement.label}
      multiple={multiple}
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      name={field.name}
      error={Boolean(fieldState.error)}
      helperText={fieldState.error?.message}
      fetchOptions={fetchOptions}
      getOptionLabel={(option) => option?.label ?? ""}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      loading={loading}
      disabled={uiSchemaElement.disabled}
      startAdornment={
        <InputAdornment position="start">
          <WbIncandescentOutlinedIcon color="error" />
        </InputAdornment>
      }
    />
  );
};
